import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { createApi } from "@reduxjs/toolkit/query/react";
import { endpoints, baseQuery, formatErrors } from "utils/api/endpoints";
import { SimServiceState, SimServiceData, ViewQrResponse, PortApiResponse, PortStatus } from "./types";
import { selectUser } from "app/slice/selectors";

export const initialState: SimServiceState = {
  loading: false,
  error: null,
  data: null,
  portStatus: null,
};

interface ApiResponse {
  status: number;
  statusText: string;
  data: SimServiceData;
  meta: {
    requestId: string;
    url: string;
  };
}

// Helper function to parse port response
const parsePortResponse = (response: PortApiResponse): PortStatus => {
  const { wholeSaleApi, errorMessage } = response.data;
  const { wholeSaleResponse } = wholeSaleApi;
  const { result } = wholeSaleResponse;
  const { addPortResponse } = result.responseDetails;
  const { portResponse, portingLocator } = addPortResponse;
  
  const isSuccess = portResponse.portRequestStatus === "CO";
  const isFailed = portResponse.portRequestStatus === "CT";
  const isInProgress = portResponse.portRequestStatus === "CF";
  
  return {
    isSuccess,
    isFailed,
    isInProgress,
    statusCode: wholeSaleResponse.statusCode,
    description: wholeSaleResponse.description,
    portRequestNumber: portingLocator.portRequestNumber,
    teleportPortRecordId: portingLocator.teleportPortRecordId,
    billingAccountNumber: portResponse.billingAccountNumber,
    errorMessage: isFailed ? errorMessage : undefined,
  };
};

const slice = createSlice({
  name: "simService",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setData(state, action: PayloadAction<SimServiceData>) {
      state.data = action.payload;
    },
    setPortStatus(state, action: PayloadAction<PortStatus | null>) {
      state.portStatus = action.payload;
    },
  },
});

// Create RTK Query API
export const api = createApi({
  reducerPath: "simServiceApi",
  baseQuery,
  tagTypes: ["SimService"],
  endpoints: (builder) => ({
    getSimService: builder.query<ApiResponse, { customerId: string }>({
      query: (params) => ({
        ...endpoints.simService,
        params,
      }),
      providesTags: ["SimService"],
      // Add retry logic
      extraOptions: {
        maxRetries: 3,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 30000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
    }),
    transferNumber:builder.mutation<PortApiResponse, any>({
      query:body => ({
        ...endpoints.transferNumber,
        body
      }),
      transformResponse: (response: PortApiResponse) => {
        return response;
      },
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
      invalidatesTags:["SimService"]
    }),
    activateSim: builder.mutation<any, any>({
      query: body => ({
        ...endpoints.activateSim,
        body
      }),
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
      invalidatesTags: ["SimService"]
    }),
    viewQr: builder.mutation<Blob, any>({
      query: body => ({
        ...endpoints.viewQr,
        body,
        responseHandler: async (response) => {
          return response.blob();
        },
      }),
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
      invalidatesTags: ["SimService"]
    }),
    checkPortStatus: builder.mutation<any, any>({
      query: body => ({
        ...endpoints.checkPortStatus,
        body,
      }),
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
      invalidatesTags: ["SimService"]
    }),
    updatePortin: builder.mutation<PortApiResponse, any>({
      query: body => ({
        ...endpoints.updatePortin,
        body,
      }),
      transformResponse: (response: PortApiResponse) => {
        return response;
      },
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
      invalidatesTags: ["SimService"]
    }),
    lockUnlockSim: builder.mutation<any, any>({
      query: body => ({
        ...endpoints.lockUnlockSim,
        body,
      }),
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
      invalidatesTags: ["SimService"]
    }),
    registerPSim: builder.mutation<any, any>({
      query: body => ({
        ...endpoints.registerPSim,
        body,
      }),
      transformErrorResponse: (response: { status: number, data: any }) => {
        return formatErrors(response.data);
      },
      invalidatesTags: ["SimService"]
    })
  }),
});

export const { actions: simServiceActions, reducer: simServiceReducer } = slice;

// Selectors
export const selectSimService = (state: any) => state.simService;
export const selectPortStatus = (state: any) => state.simService?.portStatus;
export const selectIsPortSuccess = (state: any) => state.simService?.portStatus?.isSuccess;
export const selectIsPortFailed = (state: any) => state.simService?.portStatus?.isFailed;

export const useSimServiceSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return { 
    actions: slice.actions,
    ...api
  };
};

/**
 * Example Usage:
 *
 * export function SimServiceComponent() {
 *   const { actions, useGetSimServiceQuery } = useSimServiceSlice();
 *   const customerId = "your-customer-id";
 *   const { data, isLoading, error } = useGetSimServiceQuery(customerId);
 *
 *   useEffect(() => {
 *     if (data) {
 *       // Handle sim service data
 *     }
 *   }, [data]);
 * }
 */
